<template>
  <my-giving-layout>
    <!-- Header with back button -->
    <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
    <div class="px-[24px] pt-[24px] pb-[32px] min-h-[calc(100vh-140px)] flex flex-col">
      <!-- Content centered vertically -->
      <div class="flex-1 flex flex-col justify-center items-center text-center mb-[32px]">
        <!-- App and Bank Icons -->
        <div class="flex items-center justify-center gap-4 mb-[32px]">
          <!-- App Icon -->
          <div
            class="w-[72px] h-[72px] bg-[#FED970] rounded-[16px] shadow-lg flex items-center justify-center border border-gray-200"
          >
            <img src="/assets/icons/logo-hogc.svg" alt="HOGC Logo" class="w-[48px] h-[48px]" />
          </div>

          <!-- Link Icon -->
          <div class="w-[20px] h-[20px] flex items-center justify-center">
            <img src="/assets/icons/link.svg" alt="Link Logo" class="w-[32px] h-[32px]" />
          </div>

          <!-- Bank Icon -->
          <div
            class="w-[72px] h-[72px] bg-[#B10A321A] rounded-[16px] shadow-lg flex items-center justify-center border border-gray-200"
          >
            <img :src="selectedBank.iconPath" :alt="selectedBank.name" class="w-[48px] h-[48px]" />
          </div>
        </div>

        <!-- Redirect Text -->
        <p class="text-[1rem] text-gray-600 mb-[8px]">You will be redirected to</p>

        <!-- Bank Name -->
        <h1 class="text-[1.75rem] font-[700] text-gray-900 mb-[40px]">
          {{ selectedBank.name }}
        </h1>

        <!-- Description Text -->
        <p
          class="text-[0.875rem] text-gray-600 leading-[1.5rem] mb-[24px] border-t-[1px] border-[#C3C2C1] pt-[36px]"
        >
          Link your own bank account to give fast and securely, without leaving the app.
        </p>
      </div>

      <!-- Continue Button - Anchored to bottom with minimum 32px spacing from content -->
      <div class="mt-auto">
        <button
          @click="continueToBank"
          :disabled="isLoading"
          class="w-full bg-[#42362B] text-white font-semibold rounded-[100px] py-[16px] text-center text-[1.125rem]"
          :class="{ 'bg-[#C3C2C1]': isLoading }"
        >
          {{ isLoading ? 'Loading...' : 'Continue' }}
        </button>
      </div>
    </div>
  </my-giving-layout>
</template>

<script>
import BackButton from '@/components/common/BackButton.vue';
import axios from 'axios';
import { openUrl } from '@/utils/browser-helper';
import { BANK_ENUM } from '../../../constants/MyGiving/bank-list';
import MyGivingLayout from '@/components/MyGivingLayout.vue';

export default {
  name: 'EgiroBankConfirmation',
  components: {
    BackButton,
    MyGivingLayout,
  },
  data() {
    return {
      selectedBank: null,
      isLoading: false,
    };
  },
  created() {
    // Get bankCode from route params
    const bankCode = this.$route.params.bankCode;

    // Match and assign the bank object
    this.selectedBank = BANK_ENUM[bankCode] || null;

    // Optional: Handle invalid bank code
    if (!this.selectedBank) {
      console.error('Invalid bank code:', bankCode);
      // You can redirect to error page or bank list page
      // this.$router.push('/my-giving/link-bank');
    }
  },
  methods: {
    back() {
      this.$router.push('/my-giving/list-of-banks');
    },
    getBankName(bankCode) {
      return this.bankNames[bankCode] || bankCode;
    },
    async continueToBank() {
      try {
        this.isLoading = true;

        // Check if user came from recurring giving flow
        const isFromRecurringGiving =
          this.$route.query.fromrecurring === 'true' || this.$route.query.fromrecurring === true;

        // Set sessionStorage flag before opening bank URL
        if (isFromRecurringGiving) {
          sessionStorage.setItem('isFromRecurringGiving', 'true');
        }

        const payload = {
          applicantBankCode: this.$route.params.bankCode,
        };

        const response = await axios.post(
          `${process.env.VUE_APP_GIVING_API_V2_URL}/e-giro/authorize/creation`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.user.token}`,
            },
          },
        );

        // The API returns the redirect URL
        const redirectUrl = response.data;

        if (redirectUrl) {
          // Redirect to the bank's authentication page
          await openUrl(
            { url: redirectUrl },
            {
              title: 'Link Bank',
              source: 'link-bank',
              page: 'MyGiving',
            },
          );
        } else {
          throw new Error('No redirect URL received from server');
        }
      } catch (error) {
        console.error('Error during bank authorization:', error);

        // Show user-friendly error message
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Failed to connect to bank. Please try again.';

        alert(errorMessage); // TODO: Replace with proper error handling/toast
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Inter';
}
</style>
